import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

export interface BestPostingTime {
  id: string;
  organization_id: string;
  platform: string;
  day_of_week: number;
  hour_of_day: number;
  engagement_score: number;
  sample_size: number;
  last_calculated_at: string;
  created_at: string;
}

export interface BestTimeRecommendation {
  dayOfWeek: number;
  hourOfDay: number;
  engagementScore: number;
  dayName: string;
  timeSlot: string;
}

export class BestTimesService {
  private static readonly TABLE = 'best_posting_times';
  private static readonly MIN_CLICKS_THRESHOLD = 1000;
  private static logger = logger.child({ component: 'BestTimesService' });

  private static readonly DAY_NAMES = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
  ];

  /**
   * Get best posting times for organization and platform
   */
  static async getBestTimes(
    organizationId: string,
    platform: string,
    topN: number = 10
  ): Promise<BestTimeRecommendation[]> {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('organization_id', organizationId)
        .eq('platform', platform.toLowerCase())
        .order('engagement_score', { ascending: false })
        .limit(topN);

      if (error) {
        this.logger.error('Failed to fetch best posting times', error, { 
          organizationId, 
          platform 
        });
        throw error;
      }

      return (data || []).map(time => this.formatRecommendation(time));
    }, { operation: 'getBestTimes', table: this.TABLE });
  }

  /**
   * Get best times for a specific day and platform
   */
  static async getBestTimesForDay(
    organizationId: string,
    platform: string,
    dayOfWeek: number
  ): Promise<BestTimeRecommendation[]> {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('organization_id', organizationId)
        .eq('platform', platform.toLowerCase())
        .eq('day_of_week', dayOfWeek)
        .order('engagement_score', { ascending: false });

      if (error) {
        this.logger.error('Failed to fetch best times for day', error, { 
          organizationId, 
          platform, 
          dayOfWeek 
        });
        throw error;
      }

      return (data || []).map(time => this.formatRecommendation(time));
    }, { operation: 'getBestTimesForDay', table: this.TABLE });
  }

  /**
   * Get total clicks for organization to validate data sufficiency
   */
  static async getTotalClicks(organizationId: string): Promise<number> {
    return withServiceErrorHandling(async () => {
      const { count, error } = await supabase
        .from('link_clicks')
        .select('id, short_links!inner(organization_id)', { count: 'exact', head: true })
        .eq('short_links.organization_id', organizationId);

      if (error) {
        this.logger.error('Failed to get total clicks', error, { organizationId });
        throw error;
      }

      return count || 0;
    }, { operation: 'getTotalClicks', table: 'link_clicks' });
  }

  /**
   * Get recommended posting time for a specific date
   * Returns null if organization has < 1000 clicks (insufficient data for valid recommendations)
   */
  static async getRecommendedTime(
    organizationId: string,
    platform: string,
    date: Date
  ): Promise<BestTimeRecommendation | null> {
    return withServiceErrorHandling(async () => {
      // Check if org has enough data for statistically valid recommendations
      const totalClicks = await this.getTotalClicks(organizationId);
      if (totalClicks < this.MIN_CLICKS_THRESHOLD) {
        this.logger.info('Insufficient click data for best times recommendation', { 
          organizationId, 
          totalClicks, 
          threshold: this.MIN_CLICKS_THRESHOLD 
        });
        return null;
      }

      const dayOfWeek = date.getDay();
      
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('organization_id', organizationId)
        .eq('platform', platform.toLowerCase())
        .eq('day_of_week', dayOfWeek)
        .order('engagement_score', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        this.logger.error('Failed to get recommended time', error, { 
          organizationId, 
          platform, 
          date 
        });
        throw error;
      }

      return data ? this.formatRecommendation(data) : null;
    }, { operation: 'getRecommendedTime', table: this.TABLE });
  }

  /**
   * Get heatmap data for calendar visualization
   */
  static async getHeatmapData(
    organizationId: string,
    platform: string
  ): Promise<Map<string, number>> {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('day_of_week, hour_of_day, engagement_score')
        .eq('organization_id', organizationId)
        .eq('platform', platform.toLowerCase());

      if (error) {
        this.logger.error('Failed to fetch heatmap data', error, { 
          organizationId, 
          platform 
        });
        throw error;
      }

      const heatmap = new Map<string, number>();
      data?.forEach(time => {
        const key = `${time.day_of_week}-${time.hour_of_day}`;
        heatmap.set(key, time.engagement_score);
      });

      return heatmap;
    }, { operation: 'getHeatmapData', table: this.TABLE });
  }

  /**
   * Format a best posting time into a recommendation
   */
  private static formatRecommendation(time: BestPostingTime): BestTimeRecommendation {
    const hour12 = time.hour_of_day % 12 || 12;
    const ampm = time.hour_of_day < 12 ? 'AM' : 'PM';
    const timeSlot = `${hour12}:00 ${ampm}`;

    return {
      dayOfWeek: time.day_of_week,
      hourOfDay: time.hour_of_day,
      engagementScore: time.engagement_score,
      dayName: this.DAY_NAMES[time.day_of_week],
      timeSlot
    };
  }

  /**
   * Trigger best times analysis (calls edge function)
   */
  static async triggerAnalysis(organizationId: string): Promise<void> {
    return withServiceErrorHandling(async () => {
      const { error } = await supabase.functions.invoke('analyze-best-times', {
        body: { organizationId }
      });

      if (error) {
        this.logger.error('Failed to trigger best times analysis', error, { 
          organizationId 
        });
        throw error;
      }

      this.logger.info('Best times analysis triggered', { organizationId });
    }, { operation: 'triggerAnalysis', table: this.TABLE });
  }
}
